import { usePlatformConfig } from '~/context/platform'
import { useUser } from '~/context/user'
import { shouldUseImperialUnits } from '~/utils/userCountry'

export type RoomDimensions = {
  sqFeet?: number
  sqMeters?: number
}

export type RoomArea = {
  value: number
  unit: 'imperial' | 'metric'
}

/**
 * Single source of truth for the unit system used to display measurements
 * (currently room dimensions) so that every step of a journey agrees.
 */
const useImperialUnits = (): boolean => {
  const { userCountry } = useUser()
  const { opCo } = usePlatformConfig()

  return shouldUseImperialUnits(userCountry, opCo)
}

/**
 * Picks the room area matching the active unit system. Returns undefined when
 * that system's value is missing or zero, so callers hide the room size
 * instead of rendering a converted or zero value.
 */
export const selectRoomArea = (
  dimensions: RoomDimensions | undefined,
  shouldUseSqFt: boolean
): RoomArea | undefined => {
  const value = shouldUseSqFt ? dimensions?.sqFeet : dimensions?.sqMeters

  return value ? { value, unit: shouldUseSqFt ? 'imperial' : 'metric' } : undefined
}

export default useImperialUnits
